import React, { useState } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import SearchOffIcon from '@mui/icons-material/SearchOff'
import { StockSearchContext } from './types/types'
import { Stock } from '../../../interfaces'
import { ApiResponse } from '../../../typings/ApiResponse'
import { eTypePosition } from '../../../enums/eTypePosition'
import { LocationCard } from './Components/PositionCard'
import {
  PositionFilter,
  PositionFilterToggle
} from './Components/PositionFilterToggle'
import { skuResultsStyles as s } from './Styles/StockBySkuResults.styles'
import { BackButtonBase } from '../../../components/_WMS/BackButton/BackButtonBase'
import { InfoChip } from '../../../components/_WMS/InfoChip/InfoChip'

interface StockBySkuResultsProps {
  data: ApiResponse<Stock>
  context: StockSearchContext
  onBack: () => void
  onFilterChange: (context: StockSearchContext, typePositionId?: number) => void
  isRefetching?: boolean
}

// ── Main component ────────────────────────────────────────────────────────────

const StockBySkuResults: React.FC<StockBySkuResultsProps> = ({
  data,
  context,
  onBack,
  onFilterChange,
  isRefetching = false
}) => {
  const [positionFilter, setPositionFilter] = useState<PositionFilter>('sale')

  const { items } = data
  const totalQuantityDisplay = items.find(
    (stock) => stock.totalQuantity !== '00' && stock.totalQuantity !== undefined
  )?.totalQuantity
  const isEmpty = items.length === 0

  const handlePositionFilterChange = (filter: PositionFilter) => {
    console.log(filter)
    if (filter === positionFilter) return
    setPositionFilter(filter)
    onFilterChange(context, filter === 'sale' ? eTypePosition.Venta : undefined)
  }

  return (
    <Stack spacing={0.5}>
      <Typography variant='h6' component='h1' sx={s.sectionLabel}>
        SKU consultado
      </Typography>

      <Box sx={s.contextCard}>
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='center'
          mb={1.5}
        >
          <BackButtonBase onBack={onBack} />
          <InfoChip label={`Marca: ${context.selectedLabel}`} sx={s.infoChip} />
        </Stack>

        <Stack spacing={0.75} alignItems='center' justifyContent='center'>
          <InfoChip label={`SKU: ${context.searchValue}`} sx={s.infoChip} />
          <Stack direction='row' spacing={1}>
            <InfoChip
              label={`FEFO: ${context.fefo ? 'Sí' : 'No'}`}
              sx={s.infoChip}
            />
            <InfoChip
              label={`VEV: ${context.vev ? 'Sí' : 'No'}`}
              sx={s.infoChip}
            />
          </Stack>
        </Stack>
      </Box>

      {isEmpty ? (
        <Box sx={s.emptyState}>
          <SearchOffIcon sx={s.emptyIcon} />
          <Typography fontSize={13} color='text.secondary'>
            No se encontraron ubicaciones para este producto.
          </Typography>
        </Box>
      ) : (
        <>
          <Stack direction='row' spacing={1.5}>
            {[
              { label: 'Ubicaciones', value: data.totalItems },
              { label: 'Uds. totales', value: totalQuantityDisplay }
            ].map(({ label, value }) => (
              <Box key={label} sx={s.metricCard}>
                <Typography sx={s.metricLabel}>{label}</Typography>
                <Typography sx={s.metricValue}>{value}</Typography>
              </Box>
            ))}
          </Stack>

          <Stack spacing={0.75}>
            <Typography variant='caption' sx={s.toggleLabel}>
              Tipo de ubicaciones a mostrar
            </Typography>
            <PositionFilterToggle
              value={positionFilter}
              onChange={handlePositionFilterChange}
              disabled={isRefetching}
            />
          </Stack>

          <Stack spacing={0.5}>
            {items.map((item) => (
              <LocationCard
                key={`${item.stockKeepingUnitId}-${item.positionId}`}
                item={item}
              />
            ))}
          </Stack>
        </>
      )}
    </Stack>
  )
}

export default StockBySkuResults
